/**
 * Die Rückfrage vor dem Zusammenführen zweier Plätze.
 *
 * Ein `confirm()` täte es hier nicht. Es kann nur ja oder nein, und die Frage
 * hat drei Antworten: zusammenführen und diesen Namen behalten, zusammenführen
 * und jenen behalten, oder gar nicht. Der Name entscheidet, was danach an allen
 * betroffenen Tauchgängen steht — das ist keine Kleinigkeit, die man aus der
 * Reihenfolge zweier Markierungen ableiten sollte.
 *
 * Gezeigt werden deshalb beide Sätze nebeneinander mit dem, was den Ausschlag
 * gibt: Tauchgangszahl, Koordinaten, Land. Vorausgewählt ist die Empfehlung aus
 * `verortung.js`, überstimmen kann man sie mit einem Klick.
 *
 * Das Aufräumen folgt dem Sammelformular: drei Wege ans Ende, ein Riegel
 * dagegen, dass sie sich in die Quere kommen. Der Grund steht dort
 * ausführlich — `close` kommt nicht auf jeder Plattform an.
 */

import { koordinaten } from '../format.js';
import { empfehlung, tauchgangszahl, verschmelzfolgen } from '../verortung.js';
import { alsBlatt } from './blatt.js';

function seite(eintrag, gewaehlt) {
  const kennung = `bleibt-${eintrag.satz.id}`;
  const ort = koordinaten(eintrag.satz.lat, eintrag.satz.lon);
  return `<label class="verschmelzwahl${gewaehlt ? ' gewaehlt' : ''}" for="${kennung}">
      <input type="radio" name="bleibt" id="${kennung}" value="${eintrag.satz.id}"
        ${gewaehlt ? 'checked' : ''}>
      <span class="verschmelzname">${eintrag.satz.name ?? 'ohne Namen'}</span>
      <span class="verschmelzzahl">${tauchgangszahl(eintrag.anzahl)}</span>
      <span class="verschmelzort">${ort ?? 'ohne Koordinaten'}</span>
      <span class="verschmelzland">${eintrag.satz.country ?? ''}</span>
    </label>`;
}

/**
 * Fragt, welcher Satz bleibt. Zurück kommt der bleibende Eintrag oder `null`.
 *
 * `eins` und `zwei` sind `{ satz, anzahl }` — der Stammsatz und seine
 * Tauchgangszahl.
 */
export function frageZusammenfuehren(eins, zwei) {
  return new Promise((fertig) => {
    const rat = empfehlung(eins, zwei);
    const dialog = document.createElement('dialog');
    dialog.className = 'sammelformular verschmelzdialog';
    dialog.innerHTML = `
      <form method="dialog">
        <h2>Zusammenführen</h2>
        <p class="hinweis">Welcher Platz bleibt? Sein Name steht danach an allen
          Tauchgängen beider Plätze.</p>
        <div class="verschmelzwahlen">
          ${seite(eins, rat === eins)}
          ${seite(zwei, rat === zwei)}
        </div>
        <p class="wirkung"></p>
        <p class="meldung" role="status" aria-live="polite"></p>
        <div class="dialogfuss">
          <button type="button" class="knopf abbrechen">Abbrechen</button>
          <button type="button" class="knopf verschmelzen">Zusammenführen</button>
        </div>
      </form>`;
    document.body.append(dialog);

    const wirkung = dialog.querySelector('.wirkung');
    const meldung = dialog.querySelector('.meldung');
    const gewaehlt = () => (dialog.querySelector('input[name=bleibt]:checked')?.value
      === eins.satz.id ? eins : zwei);

    function zeigeWirkung() {
      const bleibt = gewaehlt();
      const weicht = bleibt === eins ? zwei : eins;
      wirkung.textContent = verschmelzfolgen(bleibt, weicht);
      for (const feld of dialog.querySelectorAll('.verschmelzwahl')) {
        feld.classList.toggle('gewaehlt', feld.querySelector('input').checked);
      }
    }
    zeigeWirkung();
    dialog.addEventListener('change', zeigeWirkung);

    // Die Entscheidung steht **vor** dem Schliessen fest, nicht im Empfänger.
    // `close()` kann das Ereignis synchron zustellen; ein Empfänger, der dann
    // `null` meldet, käme dem Knopf zuvor und bräche das Zusammenführen
    // stillschweigend ab. So gewinnt der erste Weg mit demselben Ergebnis,
    // gleich welcher es ist.
    let wahl = null;
    let erledigt = false;
    const beende = () => {
      if (erledigt) return;
      erledigt = true;
      dialog.remove();
      fertig(wahl);
    };
    dialog.addEventListener('close', beende);
    dialog.addEventListener('keydown', (e) => { if (e.key === 'Escape') beende(); });
    dialog.querySelector('.abbrechen').addEventListener('click', () => {
      dialog.close();
      beende();
    });
    dialog.querySelector('.verschmelzen').addEventListener('click', () => {
      wahl = gewaehlt();
      meldung.textContent = 'Führt zusammen …';
      dialog.close();
      beende();
    });

    alsBlatt(dialog);
    dialog.showModal();
  });
}
